import clsx from 'clsx'
import type { ParsedUrlQuery } from 'querystring'
import { useState, type FunctionComponent } from 'react'

import { useTranslate } from '../../contexts/TranslationContext'
import SvgFilter from '../../svg/icons/Filter'
import { Button } from '../shared/Button'
import { Collapsible } from '../shared/Collapsible'
import { Hidden } from '../shared/Hidden'
import { Modal } from '../shared/Modal'
import { CheckboxGroup } from './CheckboxGroup'
import style from './CheckboxGroupComponent.module.sass'

type CheckboxGroupComponentProps = {
	items: { id: string; name: string }[]
	itemsSecond?: { id: string; name: string }[]
	sum: number
	title?: string
	isFilter?: boolean
	titleSmall?: boolean
	defaultChecked?: string[]
	initialQuery: ParsedUrlQuery
	label: string
	labelSecond?: string
	isMobileMirrored?: boolean
}

export const CheckboxGroupComponent: FunctionComponent<CheckboxGroupComponentProps> = ({
	items,
	itemsSecond,
	title,
	sum,
	isFilter = false,
	titleSmall = false,
	defaultChecked,
	initialQuery,
	label,
	labelSecond,
	isMobileMirrored = false,
}) => {
	const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false)
	const translate = useTranslate()

	const initialQueryLength = Object.values(initialQuery).flat().length

	return (
		<div className={style.wrapper}>
			{isFilter ? (
				<>
					<Hidden breakpoint="mobileDown">
						{title && (
							<div className={style.header}>
								{isMobileMirrored ? (
									<div className={style.title}>{title}</div>
								) : (
									<>
										<h2 className={style.title}>{title}</h2>
									</>
								)}
							</div>
						)}
						<div className={clsx(style.checkboxGroupWrapper, style.checkboxGroupWrapperSmallerMargin)}>
							<CheckboxGroup items={items} label={label} defaultChecked={defaultChecked} initialQuery={initialQuery} />
						</div>
						{itemsSecond && labelSecond && itemsSecond.length > 0 && (
							<div className={clsx(style.checkboxGroupWrapper, style.checkboxGroupWrapperSmallerMargin)}>
								<CheckboxGroup
									items={itemsSecond}
									label={labelSecond}
									defaultChecked={defaultChecked}
									initialQuery={initialQuery}
								/>
							</div>
						)}
					</Hidden>
					<Hidden breakpoint="mobileUp">
						<div className={style.buttonWrapperMobile}>
							<button className={style.buttonMobile} onClick={() => setIsMobileFilterOpen(true)}>
								<SvgFilter />
								<span className={style.buttonText}>
									{title} {initialQueryLength > 0 && `(${initialQueryLength})`}
								</span>
							</button>
						</div>
						<Collapsible open={isMobileFilterOpen} hasHighIndex>
							<Modal close={() => setIsMobileFilterOpen(false)} isOpen={isMobileFilterOpen} type="filter">
								<div className={style.title}>{title}</div>
								<div className={style.checkboxGroupWrapper}>
									<CheckboxGroup
										items={items}
										label={label}
										defaultChecked={defaultChecked}
										initialQuery={initialQuery}
									/>
								</div>
								{itemsSecond && labelSecond && itemsSecond.length > 0 && (
									<div className={style.checkboxGroupWrapper}>
										<CheckboxGroup
											items={itemsSecond}
											label={labelSecond}
											defaultChecked={defaultChecked}
											initialQuery={initialQuery}
										/>
									</div>
								)}

								<div className={style.buttonGroup}>
									<div className={style.buttonFootWrapper}>
										<Button
											label={translate('checkboxGroup.modal.button.close')}
											type={{ variant: 'naked' }}
											onClick={() => setIsMobileFilterOpen(false)}
										/>
									</div>
									<div className={style.buttonFootWrapper}>
										<Button
											label={`${translate('checkboxGroup.modal.button.apply')}${
												typeof sum === 'number' && initialQueryLength > 0 ? ` (${sum})` : ''
											}`}
											disabled={sum === 0}
											onClick={() => setIsMobileFilterOpen(false)}
										/>
									</div>
								</div>
							</Modal>
						</Collapsible>
					</Hidden>
				</>
			) : (
				<>
					<div className={clsx(style.title, titleSmall && style.titleSmall)}>{title}</div>
					<div className={clsx(style.checkboxGroupWrapper, titleSmall && style.checkboxGroupWrapperSmallerMargin)}>
						<CheckboxGroup items={items} defaultChecked={defaultChecked} label={label} initialQuery={initialQuery} />
					</div>
				</>
			)}
		</div>
	)
}
